import { NavLink } from "react-router-dom";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";

/**
 * Routed, horizontally-scrollable tab strip used by the portal hub pages
 * (My Account, Producer Studio, Manage). Content is switched by the router
 * <Outlet/> below this strip — these "tabs" are just deep-linkable NavLinks.
 *
 * Per design_guidelines.md → portal_hub_pages_and_navigation:
 *  - active state = copper underline (inset shadow) on a muted pill (calm, theme-safe)
 *  - sticks just under the sticky top bar via --portal-topbar-offset
 *  - scrolls horizontally on mobile (never wraps)
 *  - neutral count badges, hidden when zero, capped at 99+
 */
const tabClass = ({ isActive }) =>
  `inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
    isActive
      ? "bg-muted text-foreground shadow-[inset_0_-2px_0_0_hsl(var(--primary))]"
      : "text-muted-foreground hover:bg-muted/60 hover:text-foreground"
  }`;

export function HubTabs({ testIdPrefix, tabs }) {
  return (
    <div
      className="sticky top-[var(--portal-topbar-offset,64px)] z-20 -mx-4 bg-background/95 px-4 backdrop-blur supports-[backdrop-filter]:bg-background/80 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8"
      data-testid={`${testIdPrefix}-hub-tabs`}
    >
      <ScrollArea className="w-full">
        <nav className="flex w-max min-w-full items-center gap-1 py-1.5" aria-label={`${testIdPrefix} sections`}>
          {tabs.map((t) => (
            <NavLink
              key={t.key}
              to={t.to}
              end={t.end}
              className={tabClass}
              data-testid={`${testIdPrefix}-hub-tab-link-${t.key}`}
              aria-label={t.badge > 0 ? `${t.label}, ${t.badge > 99 ? "99+" : t.badge} new` : undefined}
            >
              {t.icon && <t.icon className="h-4 w-4" aria-hidden="true" />}
              <span>{t.label}</span>
              {t.badge > 0 && (
                <Badge
                  variant="secondary"
                  className="ml-0.5 h-5 min-w-5 justify-center border border-border bg-background px-1.5 text-[11px] text-foreground"
                  data-testid={`${testIdPrefix}-hub-tab-badge-${t.key}`}
                >
                  {t.badge > 99 ? "99+" : t.badge}
                </Badge>
              )}
            </NavLink>
          ))}
        </nav>
        <ScrollBar orientation="horizontal" />
      </ScrollArea>
      <Separator />
    </div>
  );
}

/**
 * Standard hub shell: small uppercase eyebrow + optional supporting line +
 * the routed tab strip. Inner pages keep their own H1 (rendered via Outlet),
 * so we intentionally avoid a competing heading here.
 */
export function HubShell({ eyebrow, description, testIdPrefix, tabs, actions, children }) {
  return (
    <div className="space-y-3" data-testid={`${testIdPrefix}-hub`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{eyebrow}</p>
          {description && <p className="mt-1 text-sm text-muted-foreground">{description}</p>}
        </div>
        {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
      </div>
      <HubTabs testIdPrefix={testIdPrefix} tabs={tabs} />
      <div className="pt-2">{children}</div>
    </div>
  );
}
